import { useEffect, useRef, lazy, Suspense } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LanguageProvider } from './contexts/LanguageContext';
import { TypographyProvider } from './contexts/TypographyContext';
import { ColorProvider } from './contexts/ColorContext';
import { AuthProvider } from './contexts/AuthContext';
import { GenerationProvider } from './contexts/GenerationContext';
import { QuotaProvider } from './contexts/QuotaContext';
import { ImageGenerationProvider } from './contexts/ImageGenerationContext';
import { CarouselGenerationProvider } from './contexts/CarouselGenerationContext';
import { useAuth } from './hooks/useAuth';
import { useVersionCheck } from './hooks/useVersionCheck';
import { isKnownAppPath } from './hooks/useUrlSyncedTab';
import { useLanguage } from './contexts/LanguageContext';
import { supportedLanguages } from './i18n';
import type { SupportedLanguage } from './i18n';
import { toastService as toast } from './services/toastService';
import { authService } from './services/authService';
// Entrées immédiates (chaque utilisateur les voit au 1er render) → eager.
import { LoginPage } from './components/pages/LoginPage';
import { DashboardPage } from './components/pages/DashboardPage';
// V4-CODESPLIT-DASHBOARD — pages secondaires rarement atteintes (callbacks OAuth/Zernio,
// reset/forgot password) chargées en lazy → sorties du chunk principal. Composants feuilles
// mutuellement exclusifs (aucun état partagé) → split sans risque de régression.
const OAuthCallbackPage = lazy(() =>
  import('./components/pages/OAuthCallbackPage').then((m) => ({ default: m.OAuthCallbackPage })),
);
const ZernioCallbackPage = lazy(() =>
  import('./components/pages/LateCallbackPage').then((m) => ({ default: m.ZernioCallbackPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./components/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./components/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const GuestAcceptPage = lazy(() =>
  import('./components/pages/GuestAcceptPage').then((m) => ({ default: m.GuestAcceptPage })),
);
// V4-E2E-2026-06-11-02 — page 404 pour les routes inconnues (rendu conditionnel, pas de Router).
const NotFoundPage = lazy(() =>
  import('./components/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

// Fallback affiché pendant le chargement d'un chunk de page lazy.
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Vérifie si on est sur la page de callback OAuth
const isOAuthCallback = () => {
  return window.location.pathname.startsWith('/oauth/callback');
};

// Vérifie si on est sur la page de callback Late
const isLateCallback = () => {
  return window.location.pathname.startsWith('/socials/callback');
};

// Handle /verify-email → redirect to backend API
const isVerifyEmail = () => {
  return window.location.pathname === '/verify-email';
};

const isForgotPassword = () => window.location.pathname === '/forgot-password';
const isResetPassword = () => window.location.pathname === '/reset-password';
const isGuestAccept = () => window.location.pathname === '/guest/accept';

const GUEST_ALLOWED_PATHS = ['/dashboard', '/engagement', '/account', '/history', '/analytics'];

function AuthWrapper() {
  const { user, isLoading, hasInitialized, refreshUser, isGuest } = useAuth();
  const { t, setLanguageLocal } = useLanguage();
  const invitationHandled = useRef(false);

  // V4-GEN-WS-EVENT-LOST-RECOVERY — détecte un onglet SPA périmé (bundle d'un
  // déploiement antérieur, qui parlerait un protocole WS obsolète → génération
  // qui échoue en silence) et propose un rechargement NON destructif (l'utilisateur
  // clique ; pas de reload forcé qui perdrait un brouillon en cours d'édition).
  useVersionCheck({
    enabled: !!user,
    onUpdateAvailable: () => {
      toast.info(
        t.toasts.updateAvailableTitle,
        t.toasts.updateAvailableDesc,
        { label: t.toasts.reload, onClick: () => window.location.reload() },
      );
    },
  });

  // Hydrate UI language from the authenticated user object.
  // Uses setLanguageLocal (no backend write-back) to avoid a persist loop.
  // Backend value takes precedence over localStorage on every login/boot.
  useEffect(() => {
    if (!user?.language) return;
    const validCodes = supportedLanguages.map((l) => l.code);
    if (validCodes.includes(user.language as SupportedLanguage)) {
      setLanguageLocal(user.language as SupportedLanguage);
    }
  }, [user?.language, setLanguageLocal]);

  // V4-FRONT-URL-ROUTE (2026-05-29) — Sync URL avec l'état d'auth.
  // L'app utilise du rendu conditionnel (pas de React Router pour les routes principales),
  // donc rien ne mettait à jour l'URL après login/logout/expiry. Symptôme : URL bloquée
  // sur `/login` ou `/` malgré dashboard affiché → bookmarks/deep-links/analytics cassés.
  // Fix : useEffect qui replaceState selon `user` et le path actuel, en préservant
  // les query strings (Stripe ?signup=success / ?topup=success sont nettoyés par
  // l'effect dédié plus bas dans le même render cycle).
  useEffect(() => {
    if (isLoading) return;

    // Ne pas toucher l'URL sur les pages spéciales (OAuth callbacks, verify email, etc.)
    if (
      isOAuthCallback() ||
      isLateCallback() ||
      isVerifyEmail() ||
      isForgotPassword() ||
      isResetPassword() ||
      isGuestAccept()
    ) {
      return;
    }

    const path = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;

    if (user && (path === '/login' || path === '/' || path === '')) {
      window.history.replaceState({}, '', `/dashboard${search}${hash}`);
    } else if (user && isGuest && !GUEST_ALLOWED_PATHS.some((p) => path.startsWith(p))) {
      window.location.replace(`/dashboard${search}${hash}`);
    } else if (!user && path !== '/login') {
      window.history.replaceState({}, '', `/login${search}${hash}`);
    }
  }, [user, isGuest, isLoading]);

  // Phase 1.2 — /verify-email : auto-login via le token one-shot du lien.
  // Avant, on redirigeait vers le GET backend qui renvoyait sur /login?verified=true
  // → l'utilisateur devait RE-SAISIR ses identifiants (friction, point d'abandon).
  // Désormais on appelle POST /auth/verify-email-and-login : succès → session
  // ouverte + redirection dashboard ; échec → /login avec le bon message (le GET
  // reste comme fallback côté backend si le JS ne s'exécute pas).
  useEffect(() => {
    if (!isVerifyEmail()) return;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      window.location.replace('/login?verification=invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await authService.verifyEmailAndLogin(token);
        if (cancelled) return;
        // Reload complet sur /dashboard : l'AuthContext ré-hydrate depuis le
        // cookie + sessionStorage tout juste posés (parcours identique au login).
        window.location.replace('/dashboard?verified=true');
      } catch (err: unknown) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        const param = status === 410 ? 'expired' : 'invalid';
        window.location.replace(`/login?verification=${param}`);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle Stripe redirect query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const signupParam = params.get('signup');
    const topupParam = params.get('topup');
    if (signupParam === 'success') {
      toast.success(t.auth.signupWelcome);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (signupParam === 'cancelled') {
      toast.warning(t.auth.signupPaymentCancelled);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (topupParam === 'success') {
      toast.success(t.quota?.topupSuccessTitle ?? 'Recharge effectuée !');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (topupParam === 'cancel') {
      toast.warning(t.quota?.topupCancelledMessage ?? 'Recharge annulée.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    const switchParam = params.get('switch');
    if (switchParam === 'success') {
      toast.success(t.billing?.subscriptionCancelled ? 'Abonnement mis à jour !' : 'Subscription updated!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (switchParam === 'cancelled') {
      toast.warning('Changement annulé.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // V4-AUTH-VERIFY-LOGIN-TOAST — l'auto-login post-vérification (cf. effect
    // isVerifyEmail ci-dessus) redirige vers /dashboard?verified=true ; seul
    // SignInForm (/login) gérait ce param jusqu'ici, donc jamais atteint sur ce
    // parcours (l'utilisateur est déjà connecté, pas redirigé vers /login).
    if (params.get('verified') === 'true') {
      toast.success(t.auth.emailVerifiedWelcome);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t.auth, t.quota, t.billing]);

  // Handle invitation token for logged-in users
  useEffect(() => {
    if (!user || invitationHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const invitationToken = params.get('invitation');
    if (invitationToken) {
      invitationHandled.current = true;
      // V4-AUTH-SERVICE-SWALLOWS-ERROR-RESIDUAL 2026-07-30 — le service lève au lieu
      // de renvoyer un booléen. L'échec (lien périmé/invalide) est désormais signalé
      // par le toast de l'intercepteur apiClient ; le token est retiré de l'URL dans
      // les deux cas pour ne pas rejouer la tentative à chaque rendu.
      authService.acceptInvitation(invitationToken)
        .then(async () => {
          toast.success('Invitation accepted');
          await refreshUser();
        })
        .catch(() => { /* toast déjà émis par l'intercepteur */ })
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
        });
    }
  }, [user, refreshUser]);

  if (isForgotPassword()) return <Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense>;
  if (isResetPassword()) return <Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>;
  if (isGuestAccept()) return <Suspense fallback={<PageLoader />}><GuestAcceptPage /></Suspense>;

  // Si c'est le callback OAuth (legacy), afficher la page OAuth
  if (isOAuthCallback()) {
    return <Suspense fallback={<PageLoader />}><OAuthCallbackPage /></Suspense>;
  }

  // V4-AUTH-WRAPPER-REMOUNT-LOSES-ERROR — gater sur `hasInitialized` (bootstrap initial
  // seulement), PAS sur `isLoading` (qui repasse à `true` à chaque login()/register()) :
  // sinon ce spinner plein écran démonte LoginPage/SignInForm pendant une tentative de
  // connexion et son état d'erreur local (`setError`) se perd au remount qui suit l'échec.
  if (!hasInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // V4-E2E-2026-06-11-02 — route inconnue pour un user authentifié → 404 dédiée
  // (avant : fallback silencieux sur le dashboard). Les pages spéciales (callbacks,
  // reset/forgot, guest accept) sont déjà retournées plus haut ; les guests sur un
  // path interdit sont redirigés par l'effet de sync URL ci-dessus.
  if (!isKnownAppPath(window.location.pathname)) {
    return <Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>;
  }

  return <DashboardPage />;
}

export default function App() {
  // Zernio callback s'affiche HORS AuthProvider pour éviter que le popup
  // ne déclenche les vérifications d'auth et n'efface le token JWT partagé.
  if (isLateCallback()) {
    return (
      <LanguageProvider>
        <Suspense fallback={<PageLoader />}>
          <ZernioCallbackPage />
        </Suspense>
      </LanguageProvider>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <LanguageProvider>
        <TypographyProvider>
          <ColorProvider>
            <AuthProvider>
              <QuotaProvider>
                <GenerationProvider>
                  <ImageGenerationProvider>
                    <CarouselGenerationProvider>
                      <AuthWrapper />
                    </CarouselGenerationProvider>
                  </ImageGenerationProvider>
                </GenerationProvider>
              </QuotaProvider>
            </AuthProvider>
          </ColorProvider>
        </TypographyProvider>
      </LanguageProvider>
    </DndProvider>
  );
}
